/* AI spreadsheet import (prototype aiModal("import")): source → AI column mapping (useAiExtract "import") → review → useImportEmployees. */
import { useId, useState } from "react";
import { useAiExtract, useImportEmployees, useUploadFile } from "../../../data/hooks";
import type { AiExtractResponse, EmployeeInput, ImportResult } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Alert, Button, Chip, Icon, Modal, errMsg, useToast } from "../../../ui";
import { FIELD_LABEL, parseCsv, rowsToEmployees, SAMPLE_CSV, type FieldKey } from "./importMap";
import { validateEmployee } from "../../../domain/compliance";
import { activeCode } from "../../../lib/country";

type Phase = "source" | "review" | "done";

async function readText(f: File): Promise<string> {
  if (typeof f.text === "function") return f.text();
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result || "")); r.onerror = () => rej(r.error); r.readAsText(f); });
}

export default function AiImportModal({ onClose }: { onClose: () => void }) {
  const { L, t } = useI18n();
  const toast = useToast();
  const extract = useAiExtract();
  const upload = useUploadFile();
  const imp = useImportEmployees();
  const [phase, setPhase] = useState<Phase>("source");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [src, setSrc] = useState("");
  const [ai, setAi] = useState<AiExtractResponse | null>(null);
  const [parsed, setParsed] = useState<{ rows: Partial<EmployeeInput>[]; mapping: (FieldKey | null)[]; headers: string[] } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileId = useId();
  const textId = useId();

  const run = async (sample: boolean) => {
    setErr(null);
    try {
      let csv = text, fileKey: string | undefined, label = L("Pasted table", "جدول ملصق");
      if (sample) { csv = SAMPLE_CSV; label = "employees_from_old_system.csv"; }
      else if (file) {
        csv = await readText(file);
        fileKey = (await upload.mutateAsync({ file, name: file.name, folder: "imports" })).key;
        label = file.name;
      }
      const table = parseCsv(csv);
      if (table.length < 2) { setErr(L("Add a header row and at least one employee row (CSV).", "أضف صف العناوين وصفًا واحدًا على الأقل (CSV).")); return; }
      const p = rowsToEmployees(table);
      const r = await extract.mutateAsync({ feature: "import", fileKey, text: fileKey ? undefined : csv.slice(0, 20000), sample: sample ? "default" : undefined });
      setSrc(label + " · " + p.headers.length + L(" columns · ", " عمودًا · ") + p.rows.length + L(" rows", " صفوف"));
      setAi(r); setParsed(p); setPhase("review");
    } catch (e) { setErr(errMsg(e)); }
  };

  const doImport = async () => {
    if (!parsed) return;
    try {
      const r = await imp.mutateAsync(parsed.rows);
      setResult(r); setPhase("done");
      toast(L(r.created + " employees imported" + (r.skipped ? " · " + r.skipped + " skipped" : ""), "تم استيراد " + r.created + " موظفين" + (r.skipped ? " · تخطي " + r.skipped : "")), r.created ? "ok" : "bad");
    } catch (e) { setErr(errMsg(e)); }
  };

  const title = <><span className="aitag"><Icon name="sparkle" className="s" />AI</span> {L("Spreadsheet import mapping", "مطابقة ملفات الاستيراد")}</>;
  const rowIssues = parsed ? parsed.rows.map((r) => validateEmployee(r, activeCode())) : [];
  const readyCount = rowIssues.filter((x) => !Object.keys(x).length).length;
  const busy = extract.isPending || upload.isPending;

  let body, foot;
  if (phase === "source") {
    body = (
      <div className="stack">
        <p className="muted sm" style={{ margin: 0 }}>{L("Matches messy Excel columns to fields, fixes formats, transliterates names between Arabic and English and flags bad IBANs or duplicate IDs.", "يطابق أعمدة Excel ويصحح الصيغ ويحوّل الأسماء بين العربية والإنجليزية ويكشف الآيبان الخاطئ والهويات المكررة.")}</p>
        <div className="g g-2" style={{ gap: 10 }}>
          <label className="drop" htmlFor={fileId} style={{ cursor: "pointer", padding: 22 }}>
            <Icon name="upload" className="l" /><b>{L("Upload a file", "رفع ملف")}</b>
            <span className="sm">{file ? file.name : L("CSV export from Excel or your old system", "ملف CSV من Excel أو النظام القديم")}</span>
            <input type="file" id={fileId} hidden accept=".csv,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="import-file" />
          </label>
          <div className="drop" style={{ padding: 16, alignItems: "stretch", textAlign: "start" }}>
            <label className="sm" htmlFor={textId}><b>{L("…or paste text", "…أو الصق نصًا")}</b></label>
            <textarea className="inp" id={textId} rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={L("Paste a table with a header row", "الصق جدولًا مع صف العناوين")} data-testid="import-text" />
          </div>
        </div>
        <Button variant="ghost sm" icon="eye" onClick={() => run(true)} disabled={busy} data-testid="import-sample">{L("Try with a sample: ", "جرّب بمثال: ")}employees_from_old_system.csv · 12 {L("columns", "عمودًا")} · 3 {L("rows", "صفوف")}</Button>
        {err && <Alert tone="bad" title={L("Couldn't read the file", "تعذّرت قراءة الملف")} desc={err} testId="import-error" />}
        <div className="aiinfo"><Icon name="shield" className="s" /><span>{L("Personal data stays on approved providers only · 1 credit · nothing is saved until you import", "البيانات الشخصية تُرسل للمزودين المعتمدين فقط · رصيد واحد · لا يُحفظ شيء قبل الاستيراد")}</span></div>
      </div>
    );
    foot = <><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" icon="sparkle" loading={busy} disabled={!file && !text.trim()} onClick={() => run(false)} data-testid="import-extract">{L("Extract", "استخراج")}</Button></>;
  } else if (phase === "review" && parsed && ai) {
    const conf = Math.round(ai.confidence * 100);
    body = (
      <div className="stack" data-testid="import-review">
        <Alert tone="ok" tint title={L(Number(ai.fields.mapped ?? 0) + " of " + Number(ai.fields.columns ?? 0) + " columns mapped by AI", "طابق الذكاء " + Number(ai.fields.mapped ?? 0) + " من " + Number(ai.fields.columns ?? 0) + " عمودًا")} desc={src} />
        <div className="tw">
          <table className="tbl compact">
            <thead><tr><th>{L("Column", "العمود")}</th><th>{L("Field", "الحقل")}</th><th className="end">{L("Confidence", "الدقة")}</th></tr></thead>
            <tbody>
              {parsed.headers.map((h, i) => {
                const f = parsed.mapping[i];
                return (
                  <tr key={i} className={f ? "" : "lowconf"}>
                    <td className="sm">“{h}”</td>
                    <td>{f ? L(FIELD_LABEL[f][0], FIELD_LABEL[f][1]) : <span className="muted">{L("Ignored", "متجاهل")}</span>}</td>
                    <td className="end">{f ? <Chip tone={conf >= 95 ? "ok" : conf >= 90 ? "info" : "warn"} nodot>{conf}%</Chip> : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="tw">
          <table className="tbl compact" data-testid="import-rows">
            <thead><tr><th>#</th><th>{t("Employee")}</th><th>{L("ID / Iqama", "الهوية / الإقامة")}</th><th>{L("Check", "الفحص")}</th></tr></thead>
            <tbody>
              {parsed.rows.map((r, i) => {
                const issues = rowIssues[i];
                const keys = Object.keys(issues);
                return (
                  <tr key={i} className={keys.length ? "lowconf" : ""}>
                    <td className="num">{i + 1}</td>
                    <td>{r.nameEn || "—"}<div className="xs muted" dir="rtl">{r.nameAr}</div></td>
                    <td className="num">{r.idNumber || "—"}</td>
                    <td>{keys.length ? <Chip tone="warn">{keys.map((k) => k + ": " + issues[k]).join(" · ")}</Chip> : <Chip tone="ok">{L("Ready", "جاهز")}</Chip>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {err && <Alert tone="bad" title={L("Import failed", "فشل الاستيراد")} desc={err} testId="import-error" />}
        <div className="aiinfo"><Icon name="clock" className="s" /><span>{L(readyCount + " of " + parsed.rows.length + " rows ready · rows with issues are skipped and reported", readyCount + " من " + parsed.rows.length + " صفوف جاهزة · تُتخطى الصفوف ذات الملاحظات")} · <span className="num">{ai.model}</span></span></div>
      </div>
    );
    foot = <><Button variant="ghost" onClick={() => { setPhase("source"); setErr(null); }} data-testid="import-reset">{L("Start over", "البدء من جديد")}</Button><Button variant="pri" icon="check" loading={imp.isPending} disabled={!parsed.rows.length} onClick={doImport} data-testid="import-apply">{L("Import " + parsed.rows.length + " rows", "استيراد " + parsed.rows.length + " صفوف")}</Button></>;
  } else if (result) {
    body = (
      <div className="stack" data-testid="import-result">
        <Alert tone={result.created ? "ok" : "warn"} tint title={L(result.created + " created · " + result.skipped + " skipped", "أُنشئ " + result.created + " · تخطي " + result.skipped)} />
        {result.errors.length > 0 && (
          <ul className="core-list" data-testid="import-errors">
            {result.errors.map((e) => <li key={e.row}>{L("Row ", "الصف ")}<span className="num">{e.row}</span>: {e.message}</li>)}
          </ul>
        )}
      </div>
    );
    foot = <Button variant="pri" onClick={onClose} data-testid="import-done">{L("Done", "تم")}</Button>;
  }

  return <Modal title={title} onClose={onClose} className="wide" testId="import-modal" foot={foot}>{body}</Modal>;
}
